'use client'

import React, { useRef, useState } from 'react'

import { PauseIcon } from '../Icon/PauseIcon'

import { Box } from '@chakra-ui/react'

import type { ColorScheme } from '../../types'
import { PlayIcon } from '../Icon/PlayIcon'
import { Waveform } from '../Waveform/Waveform'
import { containerStyles, playButtonStyles } from './AudioPlayer.styles'

export type AudioPlayerProps = {
  /** Audio source URL to load into the waveform */
  url: string
  colorScheme?: ColorScheme
  /** Fired the first time (and each time) play is triggered */
  onPlay?: () => void
}

/**
 * A play/pause button paired with a Waveform scrubber. Owns the WaveSurfer
 * instance handed back from Waveform's onReady and toggles it on click.
 */
export const AudioPlayer = ({
  url,
  colorScheme = 'dark',
  onPlay,
}: AudioPlayerProps) => {
  const wsRef = useRef<unknown>(null)
  const [isPlaying, setIsPlaying] = useState(false)

  const handlePlayPause = (e: React.MouseEvent) => {
    e.stopPropagation()
    try {
      if (!wsRef.current) return
      onPlay?.()
      ;(wsRef.current as { playPause: () => void }).playPause()
      setIsPlaying((p) => !p)
    } catch {
      // wavesurfer may be in an invalid state
    }
  }

  return (
    <Box {...containerStyles}>
      <Box
        as='button'
        data-testid='audioPlayer_playPauseButton'
        onClick={handlePlayPause}
        {...playButtonStyles(colorScheme, isPlaying)}
      >
        {isPlaying ? (
          <PauseIcon color='currentColor' size={14} />
        ) : (
          <PlayIcon color='currentColor' size={14} />
        )}
      </Box>
      <Waveform
        colorScheme={colorScheme}
        onFinish={() => setIsPlaying(false)}
        onReady={(ws) => {
          wsRef.current = ws
        }}
        src={url}
        style={{ cursor: 'default' }}
      />
    </Box>
  )
}
